import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { UnidataTable, Table } from '~/components';
import {
  CENTRO_CUSTO_ABASTECIMENTO,
  CONTRATO_ABASTECIMENTO,
  LUBRIFICACAO,
  OPERACAO_ABASTECIMENTO,
  MODULO_CONSULTA_TOTALIZADOR,
  CUSTOMIZACAO_RAIZEN,
} from '~/constants/modules';
import { t, can, number } from '~/helpers';
import { getToastErrors } from '~/helpers/errorsRequest';
import { handleExportGeneric } from '~/helpers/exportReports';
import api from '~/services/api';
import {
  deleteVeiculo,
  findVeiculo,
  recordFiltering,
} from '~/store/modules/veiculo/actions';

import Form from './form';
import * as S from './styles';

function VeiculoList() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState({});

  const redux = useSelector((state) => state.veiculo);

  const totalizer = useMemo(
    () => [
      { label: 0, value: 'Não Possui' },
      { label: 1, value: t('s.operacao.abastecimento.horimetro') },
      { label: 2, value: t('s.operacao.abastecimento.hodometro') },
    ],
    []
  );

  const status = useMemo(
    () => [
      { label: 'N', value: t('s.geral.opcoes.nao') },
      { label: 'S', value: t('s.geral.opcoes.sim') },
    ],
    []
  );

  const statusDvec = useMemo(
    () =>
      can(MODULO_CONSULTA_TOTALIZADOR)
        ? [
            { label: '0', value: t('labelsistema.naopossui') },
            { label: '1', value: 'DVEC' },
            { label: '2', value: 'Autotrac' },
            { label: '4', value: 'Solinftec' },
            { label: '3', value: 'SS Telemática' },
          ]
        : [
            { label: '0', value: t('s.geral.opcoes.nao') },
            { label: '1', value: t('s.geral.opcoes.sim') },
          ],
    []
  );

  const situation = useMemo(
    () => [
      { label: 'L', value: t('s.geral.opcoes.liberado') },
      { label: 'B', value: t('s.geral.opcoes.bloqueado') },
      { label: 'C', value: t('s.geral.opcoes.cancelado') },
    ],
    []
  );

  const nivelSeguranca = useMemo(
    () => [
      { label: '0', value: t('s.geral.nivelseguranca.semleitura') },
      { label: '1', value: t('s.geral.nivelseguranca.baixo') },
      { label: '2', value: t('s.geral.nivelseguranca.medio') },
      { label: '3', value: t('s.geral.nivelseguranca.alto') },
      // { value: '0', label: t('s.geral.nivelseguranca.semleitura') },
      // { value: '1', label: t('s.geral.nivelseguranca.baixo') },
      // { value: '2', label: t('s.geral.nivelseguranca.medio') },
      // { value: '3', label: t('s.geral.nivelseguranca.alto') },
    ],
    []
  );

  const numCols = useMemo(() => {
    let numberCols = 2;

    if (can(LUBRIFICACAO)) numberCols += 1;
    if (can(OPERACAO_ABASTECIMENTO)) numberCols += 1;

    return numberCols;
  }, []);

  /// /  nivelSeguranca.find((s) => String(s.label) === String(value)).value || '',

  const handleSerializeSeguranca = useCallback(
    (value) => {
      const item = nivelSeguranca.find((seg) => seg.label === value);
      // const item = nivelSeguranca.find((seg) => seg.value === value);

      if (item) return item.value;

      return value;
    },
    [nivelSeguranca]
  );

  const handleResponseDataItems = useCallback(async (id) => {
    try {
      if (id) {
        setLoading(true);

        await api
          .get(`veiculos/detalhes/${id}`)
          .then((response) => setItems(response.data));
      }
    } catch (err) {
      getToastErrors(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const header = useMemo(() => {
    const data = [
      {
        id: 'idveiculo',
        label: t('s.cadastro.veiculo.idveiculo'),
        filter: { type: 'text' },
      },
      {
        id: 'placaveiculo',
        label: t('s.cadastro.veiculo.placaveiculo'),
        filter: { type: 'text' },
      },
      {
        id: can(CUSTOMIZACAO_RAIZEN)
          ? 'idempresaveiculo'
          : 'nomeempresaveiculo',
        label: t('s.cadastro.veiculo.nomeempresaveiculo'),
        filter: { type: 'text' },
      },
      {
        id: 'nomemodeloveiculo',
        label: t('s.cadastro.veiculo.idmodeloveiculo'),
        filter: { type: 'text' },
      },
      {
        id: 'tipototalizadorveiculo',
        label: t('s.cadastro.veiculo.tipototalizadorveiculo'),
        formatted: (value) =>
          totalizer.find((tot) => tot.label === value).value || '',
        filter: { type: 'select', options: totalizer },
      },
      {
        id: 'totalizadorveiculo',
        label: t('s.cadastro.veiculo.totalizadorveiculo'),
        align: 'right',
        formatted: (value) => number(value, 1),
        filter: { type: 'text' },
      },
      {
        id: 'tipototalizador2veiculo',
        label: t('s.cadastro.veiculo.tipototalizador2veiculo'),
        formatted: (value) =>
          totalizer.find((tot) => tot.label === value).value || '',
        filter: { type: 'select', options: totalizer },
      },
      {
        id: 'totalizador2veiculo',
        label: t('s.cadastro.veiculo.totalizador2veiculo'),
        align: 'right',
        formatted: (value) => number(value, 1),
        filter: { type: 'text' },
      },
      {
        id: 'possuidvecveiculo',
        label: can(MODULO_CONSULTA_TOTALIZADOR)
          ? t('labelsistema.leitortotalizadorveiculo')
          : t('s.cadastro.veiculo.possuidvecveiculo'),
        formatted: (value) => {
          const item = statusDvec.find((s) => s.label === String(value));
          return item ? item.value : value;
        },
        filter: { type: 'select', options: statusDvec },
      },
      {
        id: 'possuittagveiculo',
        label: t('s.cadastro.veiculo.possuittagveiculo'),
        formatted: (value) => status.find((s) => s.label === value).value || '',
        filter: { type: 'select', options: status },
      },
      {
        id: 'leituracontinuattagveiculo',
        label: t('s.geral.nivelseguranca'),
        formatted: (value) => handleSerializeSeguranca(value),
        // formatted: (value) =>
        // nivelSeguranca.find((s) => String(s.label) === String(value)).value ||  '',
        filter: { type: 'select', options: nivelSeguranca },
      },
      {
        id: 'statusveiculo',
        label: t('s.cadastro.veiculo.statusveiculo'),
        formatted: (value) =>
          situation.find((s) => s.label === value).value || '',
        filter: { type: 'select', options: situation },
      },
    ];

    if (can(CENTRO_CUSTO_ABASTECIMENTO)) {
      data.splice(4, 0, {
        id: 'nomecentrocustostr',
        label: t('s.cadastro.veiculo.idcentrocusto'),
        filter: { type: 'text' },
        align: 'left',
      });
    }

    if (can(CONTRATO_ABASTECIMENTO)) {
      data.splice(5, 0, {
        id: 'nomecontratostr',
        // id: 'idcontrato',
        label: t('s.cadastro.veiculo.idcontrato'),
        filter: { type: 'text' },
      });
    }

    return data;
  }, [totalizer, status, situation, statusDvec, nivelSeguranca]);

  const cellsStock = useMemo(
    () => [
      { id: 'codigonomeposto', label: t('s.relatorio.geral.postocomboio') },
    ],
    []
  );

  const cellsProduct = useMemo(
    () => [
      {
        id: 'codigoauxiliarproduto',
        label: t('s.relatorio.reltransferencia.idproduto'),
      },
      { id: 'nomeproduto', label: t('s.geral.camposistema.nomeproduto') },
    ],
    []
  );

  const cellsCompartiment = useMemo(
    () => [
      {
        id: 'idcompartimento',
        label: t('s.relatorio.abastecimento.idcompartimento'),
      },
      {
        id: 'descricaocompartimento',
        label: t('s.geral.camposistema.descricaocompartimentostr'),
      },
      {
        id: 'codigoauxiliarproduto',
        label: t('s.relatorio.reltransferencia.idproduto'),
      },
      {
        id: 'nomeabreviadoproduto',
        label: t('s.geral.camposistema.nomeproduto'),
      },
      {
        id: 'totalizadorveiculocompartimento',
        label: t('s.geral.camposistema.totalizadormovimentacaoabastecimento'),
      },
      {
        id: 'dataatualizacaoveiculocompartimento',
        label: t('s.operacao.estoqueproduto.dataalteracao'),
      },
      {
        id: 'transponderveiculotransponder',
        label: t('s.operacao.limpezatag.taghistoricotag'),
      },
    ],
    []
  );

  const cellsOperation = useMemo(
    () => [
      { id: 'idoperacao', label: t('s.geral.camposistema.idoperacao') },
      {
        id: 'descricaooperacao',
        label: t('s.geral.camposistema.descricaooperacao'),
      },
    ],
    []
  );

  useEffect(() => {
    if (redux.loading) {
      setItems({});
    }
  }, [redux.loading]);

  // impressao pdf ou xls

  const fieldlist = [
    'idveiculo',
    'placaveiculo',
    'nomeempresaveiculo',
    'nomemodeloveiculo',
    'maxtagveiculo',
    'tipoveiculo',
  ];

  if (can(CUSTOMIZACAO_RAIZEN)) {
    fieldlist.push('tipoauxiliarveiculo');
  }

  if (can(CENTRO_CUSTO_ABASTECIMENTO)) {
    fieldlist.push('nomecentrocustostr');
  }

  if (can(CONTRATO_ABASTECIMENTO)) {
    fieldlist.push('nomecontratostr');
  }

  fieldlist.push(
    'tipototalizadorveiculo',
    'totalizadorveiculo',
    'digitostotalizadorveiculo',
    'casasdecimaistotalizadorveiculo',
    'tipototalizador2veiculo',
    // 'totalizador2veiculo',
    // 'digitostotalizador2veiculo',
    'aceitatotforafaixa'
  );

  if (can(MODULO_CONSULTA_TOTALIZADOR)) {
    fieldlist.push('possuidvecveiculo');
  }

  fieldlist.push(
    'iddvecveiculo',
    'possuittagveiculo',
    // 'digitafrotaterminalveiculo',
    'leituracontinuattagveiculo',
    'observacaoveiculo',
    'volumetanqueveiculo',
    // 'bloqueiaabastacimatanqueveiculo',
    // 'rodagemminimaveiculo',
    // 'rodagemmaximaveiculo',
    'statusveiculo'
  );

  const substituirHeader = {
    idveiculo: t('s.cadastro.veiculo.idveiculo'),
    placaveiculo: t('s.cadastro.veiculo.placaveiculo'),
    nomeempresaveiculo: t('s.cadastro.veiculo.nomeempresaveiculo'),
    nomemodeloveiculo: t('s.cadastro.veiculo.idmodeloveiculo'),
    tipototalizadorveiculo: t('s.cadastro.veiculo.tipototalizadorveiculo'),
    totalizadorveiculo: t('s.cadastro.veiculo.totalizadorveiculo'),
    tipototalizador2veiculo: t('s.cadastro.veiculo.tipototalizador2veiculo'),
    totalizador2veiculo: t('s.cadastro.veiculo.totalizador2veiculo'),
    possuidvecveiculo: can(MODULO_CONSULTA_TOTALIZADOR)
      ? t('labelsistema.leitortotalizadorveiculo')
      : t('s.cadastro.veiculo.possuidvecveiculo'),
    possuittagveiculo: t('s.cadastro.veiculo.possuittagveiculo'),
    leituracontinuattagveiculo: t('s.geral.nivelseguranca'),
    statusveiculo: t('s.cadastro.veiculo.statusveiculo'),
    maxtagveiculo: t('s.cadastro.veiculo.maxtagveiculo'),
    observacaoveiculo: t('s.operacao.limpezatag.observacaohistoricotag'),
    nomecentrocustostr: t('s.cadastro.veiculo.idcentrocusto'),
    nomecontratostr: t('s.cadastro.veiculo.idcontrato'),
    tipoauxiliarveiculo: t('s.configuracao.comboio.tipoposto'),
    possuidvecveiculo: t('s.cadastro.veiculo.possuidvecveiculo'),
    volumetanqueveiculo: t('s.cadastro.veiculo.volumetanqueveiculo'),
    bloqueiaabastacimatanqueveiculo: t(
      's.cadastro.veiculo.bloqueiaabastacimatanqueveiculo'
    ),
    rodagemminimaveiculo: t('s.cadastro.veiculo.rodagemminimaveiculo'),
    rodagemmaximaveiculo: t('s.cadastro.veiculo.rodagemmaximaveiculo'),
    digitafrotaterminalveiculo: t(
      's.cadastro.veiculo.digitafrotaterminalveiculo'
    ),
    iddvecveiculo: t('labelsistema.codigoleitortotalizadorveiculo'),
    digitostotalizadorveiculo: t(
      's.geral.camposistema.digitostotalizadorveiculo'
    ),
    casasdecimaistotalizadorveiculo: t(
      's.geral.camposistema.casasdecimaistotalizadorveiculo'
    ),
    digitostotalizador2veiculo: t(
      's.cadastro.veiculo.digitostotalizador2veiculo'
    ),
    casasdecimaistotalizador2veiculo: t(
      's.cadastro.veiculo.casasdecimaistotalizador2veiculo'
    ),
    aceitatotforafaixa: t('s.cadastro.veiculo.aceitatotforafaixa'),
  };

  return (
    <S.Container>
      <UnidataTable
        fixed
        redux={redux}
        header={header}
        read={recordFiltering}
        find={findVeiculo}
        remove={deleteVeiculo}
        onRow={(value) => handleResponseDataItems(value.idveiculo)}
        form={Form}
        keys="idveiculo"
        detach="nomeveiculo"
        maxHeight="60vh"
        pdf
        excel
        onExport={(formato) =>
          handleExportGeneric({
            endpoint: 'veiculos',
            formato,
            fields: fieldlist,
            headers: substituirHeader,
            redux,
            ordination: redux.params.ordination,
            orderfield: redux.params.orderfield,
            title: 'Relatorio de Veiculos',
            compactQuery: true,
            substituirValores: {
              statusveiculo: {
                L: t('s.geral.opcoes.liberado'),
                B: t('s.geral.opcoes.bloqueado'),
                C: t('s.geral.opcoes.cancelado'),
              },
              possuidvecveiculo: can(MODULO_CONSULTA_TOTALIZADOR)
                ? {
                    '0': t('labelsistema.naopossui'),
                    '1': 'DVEC',
                    '2': 'Autotrac',
                    '4': 'Solinftec',
                    '3': 'SS Telemática',
                  }
                : {
                    '0': t('s.geral.opcoes.nao'),
                    '1': t('s.geral.opcoes.sim'),
                  },
              leituracontinuattagveiculo: {
                '0': t('s.geral.nivelseguranca.semleitura'),
                '1': t('s.geral.nivelseguranca.baixo'),
                '2': t('s.geral.nivelseguranca.medio'),
                '3': t('s.geral.nivelseguranca.alto'),
              },
              tipototalizadorveiculo: {
                '0': 'Não Possui',
                '1': t('s.operacao.abastecimento.horimetro'),
                '2': t('s.operacao.abastecimento.hodometro'),
              },
              tipototalizador2veiculo: {
                '0': 'Não Possui',
                '1': t('s.operacao.abastecimento.horimetro'),
                '2': t('s.operacao.abastecimento.hodometro'),
              },
              possuittagveiculo: {
                N: t('s.geral.opcoes.nao'),
                S: t('s.geral.opcoes.sim'),
              },
              tipoveiculo: {
                0: t('s.cadastro.veiculo.proprio'),
                1: t('s.cadastro.veiculo.terceiros'),
                2: t('s.cadastro.veiculo.comboio'),
              },
              tipoauxiliarveiculo: {
                V: t('s.menu.cadastro.veiculo.veiculo'),
                C: t('s.menu.cadastro.veiculo.centrocusto'),
              },
              bloqueiaabastacimatanqueveiculo: {
                N: t('s.geral.opcoes.nao'),
                S: t('s.geral.opcoes.sim'),
              },
              aceitatotforafaixa: {
                N: t('s.geral.opcoes.nao'),
                S: t('s.geral.opcoes.sim'),
              },
              // digitafrotaterminalveiculo: {
              //   0: t('s.geral.opcoes.nao'),
              //   1: t('s.geral.opcoes.sim'),
              // },
            },
          })
        }
      />

      <S.Wrapper grids={numCols}>
        <Table
          isResponsive
          size="small"
          height="18rem"
          paginate={99}
          showPagination={false}
          cells={cellsStock}
          data={items.postoscomboios ?? []}
          loding={loading}
        />

        <Table
          isResponsive
          size="small"
          height="18rem"
          paginate={99}
          showPagination={false}
          cells={cellsProduct}
          data={items.produtos ?? []}
          loding={loading}
        />

        {can(LUBRIFICACAO) && (
          <Table
            isResponsive
            size="small"
            height="18rem"
            paginate={99}
            showPagination={false}
            cells={cellsCompartiment}
            data={items.compartimentos ?? []}
            loding={loading}
          />
        )}

        {can(OPERACAO_ABASTECIMENTO) && (
          <Table
            isResponsive
            size="small"
            height="18rem"
            paginate={99}
            showPagination={false}
            cells={cellsOperation}
            data={items.operacoes ?? []}
            loding={loading}
          />
        )}
      </S.Wrapper>
    </S.Container>
  );
}

export default VeiculoList;
